import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import WhatsAppButton from "./components/WhatsAppButton";

// Lazy-loaded pages
const OurClinic = lazy(() => import("./pages/OurClinic"));
const TeamPage = lazy(() => import("./pages/Team"));
const SmileGallery = lazy(() => import("./pages/SmileGallery"));
const Blog = lazy(() => import("./pages/Blog"));
const DentalTourismCostaRica = lazy(() => import("./pages/blog/DentalTourismCostaRica"));
const SaveMoneyDentalImplants = lazy(() => import("./pages/blog/SaveMoneyDentalImplants"));
const WhatToExpectDentalTrip = lazy(() => import("./pages/blog/WhatToExpectDentalTrip"));
const QualityDentalCareAbroad = lazy(() => import("./pages/blog/QualityDentalCareAbroad"));
const PorcelainVsResinVeneers = lazy(() => import("./pages/blog/PorcelainVsResinVeneers"));
const DentalImplantsCanada = lazy(() => import("./pages/blog/DentalImplantsCanada"));
const PlayaDelCocoDentalVacation = lazy(() => import("./pages/blog/PlayaDelCocoDentalVacation"));
const FollowUpDentalSupport = lazy(() => import("./pages/blog/FollowUpDentalSupport"));
const BestDentalClinicGuanacaste = lazy(() => import("./pages/blog/BestDentalClinicGuanacaste"));
const BoneGraftDentalImplants = lazy(() => import("./pages/blog/BoneGraftDentalImplants"));
const BestTimeCostaRicaVacation = lazy(() => import("./pages/blog/BestTimeCostaRicaVacation"));
const CanIFlyAfterToothExtraction = lazy(() => import("./pages/blog/CanIFlyAfterToothExtraction"));
const PeriImplantitis = lazy(() => import("./pages/blog/PeriImplantitis"));
const ActivitiesAfterDentalSurgery = lazy(() => import("./pages/blog/ActivitiesAfterDentalSurgery"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const ThankYouEs = lazy(() => import("./pages/ThankYouEs"));
const DentalImplants = lazy(() => import("./pages/treatments/DentalImplants"));
const AllOn4 = lazy(() => import("./pages/treatments/AllOn4"));
const AllOnX = lazy(() => import("./pages/treatments/AllOnX"));
const FullMouthRestoration = lazy(() => import("./pages/treatments/FullMouthRestoration"));
const Veneers = lazy(() => import("./pages/treatments/Veneers"));
const Crowns = lazy(() => import("./pages/treatments/Crowns"));
const RootCanal = lazy(() => import("./pages/treatments/RootCanal"));
const SmileDesign = lazy(() => import("./pages/treatments/SmileDesign"));
const ImplantSupportedBridge = lazy(() => import("./pages/treatments/ImplantSupportedBridge"));
const TeethWhitening = lazy(() => import("./pages/treatments/TeethWhitening"));
const ImplantesCoronasPuentes = lazy(() => import("./pages/ImplantesCoronasPuentes"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/our-clinic" element={<OurClinic />} />
              <Route path="/our-team" element={<TeamPage />} />
              <Route path="/smile-gallery" element={<SmileGallery />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/can-i-fly-after-tooth-extraction" element={<CanIFlyAfterToothExtraction />} />
              <Route path="/blog/peri-implantitis-implant-failure" element={<PeriImplantitis />} />
              <Route path="/blog/activities-after-dental-surgery-costa-rica" element={<ActivitiesAfterDentalSurgery />} />
              <Route path="/blog/best-time-costa-rica-vacation" element={<BestTimeCostaRicaVacation />} />
              <Route path="/blog/porcelain-vs-resin-veneers" element={<PorcelainVsResinVeneers />} />
              <Route path="/blog/dental-implants-canada" element={<DentalImplantsCanada />} />
              <Route path="/blog/playa-del-coco-dental-vacation" element={<PlayaDelCocoDentalVacation />} />
              <Route path="/blog/follow-up-dental-support" element={<FollowUpDentalSupport />} />
              <Route path="/blog/best-dental-clinic-guanacaste" element={<BestDentalClinicGuanacaste />} />
              <Route path="/blog/bone-graft-dental-implants" element={<BoneGraftDentalImplants />} />
              <Route path="/blog/dental-tourism-costa-rica" element={<DentalTourismCostaRica />} />
              <Route path="/blog/save-money-dental-implants" element={<SaveMoneyDentalImplants />} />
              <Route path="/blog/what-to-expect-dental-trip" element={<WhatToExpectDentalTrip />} />
              <Route path="/blog/quality-dental-care-abroad" element={<QualityDentalCareAbroad />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/gracias" element={<ThankYouEs />} />
              <Route path="/treatments/dental-implants" element={<DentalImplants />} />
              <Route path="/treatments/all-on-4" element={<AllOn4 />} />
              <Route path="/treatments/all-on-x" element={<AllOnX />} />
              <Route path="/treatments/smile-design" element={<SmileDesign />} />
              <Route path="/treatments/full-mouth-restoration" element={<FullMouthRestoration />} />
              <Route path="/treatments/veneers" element={<Veneers />} />
              <Route path="/treatments/crowns" element={<Crowns />} />
              <Route path="/treatments/root-canal" element={<RootCanal />} />
              <Route path="/implant-supported-bridge" element={<ImplantSupportedBridge />} />
              <Route path="/treatments/teeth-whitening" element={<TeethWhitening />} />
              <Route path="/implantes-coronas-puentes" element={<ImplantesCoronasPuentes />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <WhatsAppButton />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
